import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { hasMobile, portalApi, portalSession } from './portalApi'

/**
 * Where a customer signs in.
 *
 * <p>Their mobile number and a password the caterer gave them. Not the staff
 * sign-in: this reaches one file, not a business.</p>
 */
export function CustomerLoginPage() {
  const navigate = useNavigate()
  const [mobile, setMobile] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Only set when the caterer asks for a code as well; until then this is the
  // page it always was.
  const [asking, setAsking] = useState<{ challenge: string; sentTo: string } | null>(null)
  const [code, setCode] = useState('')

  type SignInAnswer = {
    accessToken: string
    customerName: string
    mustChangePassword: boolean
    codeRequired: boolean
    challenge: string | null
    sentTo: string | null
  }

  const arrive = (result: SignInAnswer) => {
    if (result.codeRequired) {
      setAsking({ challenge: result.challenge ?? '', sentTo: result.sentTo ?? 'your mobile' })
      setPassword('')
      return
    }
    portalSession.save(result.accessToken, result.customerName)
    navigate(result.mustChangePassword ? '/my/password' : '/my', { replace: true })
  }

  const signIn = async () => {
    setBusy(true)
    setError(null)
    try {
      arrive(await portalApi.post<SignInAnswer>('/auth/login', { mobile, password }))
    } catch (problem) {
      setError(
        problem instanceof ApiError ? problem.message : 'Something went wrong. Please try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  const requestCode = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await portalApi.post<SignInAnswer>('/auth/otp/request', { mobile })
      setAsking({ challenge: result.challenge ?? '', sentTo: result.sentTo ?? 'your mobile' })
      setPassword('')
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const sendCode = async () => {
    setBusy(true)
    setError(null)
    try {
      arrive(
        await portalApi.post<SignInAnswer>('/auth/otp', {
          challenge: asking?.challenge ?? '',
          code: code.trim(),
        }),
      )
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'That code was not accepted.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Box sx={{ width: 420, maxWidth: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 2.5, color: '#7a2030' }}>
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: '#7a2030' }}>
            <RestaurantIcon />
          </Avatar>
          <Typography variant="overline" sx={{ letterSpacing: 2 }}>Shri Sangeethas</Typography>
        </Box>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Your events
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to see your enquiries, quotations, orders and bills.
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {asking ? (
            <Stack
              component="form"
              spacing={2}
              onSubmit={(event) => {
                event.preventDefault()
                if (code.trim()) void sendCode()
              }}
            >
              <Typography variant="body2">
                We sent a code to {asking.sentTo}. Type it in to finish signing in.
              </Typography>
              <TextField
                label="Code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/[^0-9]/g, ''))}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 6,
                  autoComplete: 'one-time-code',
                  'aria-label': 'Code',
                }}
                autoFocus
                required
              />
              <Button type="submit" variant="contained" size="large" disabled={busy || !code.trim()}>
                {busy ? 'Checking…' : 'Sign in'}
              </Button>
              <Button
                onClick={() => {
                  setAsking(null)
                  setCode('')
                  setError(null)
                }}
                disabled={busy}
              >
                Start again
              </Button>
            </Stack>
          ) : (
          <Stack
            component="form"
            spacing={2}
            onSubmit={(event) => {
              event.preventDefault()
              if (hasMobile(mobile) && password) void signIn()
            }}
          >
            <TextField
              label="Mobile number"
              value={mobile}
              onChange={(event) => setMobile(event.target.value)}
              autoComplete="tel"
              autoFocus
              required
              helperText="Any format works: +91, spaces or dashes"
              inputProps={{ 'aria-label': 'Mobile number' }}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              inputProps={{ 'aria-label': 'Password' }}
              InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || !hasMobile(mobile) || !password}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            <Button variant="text" disabled={busy || !hasMobile(mobile)} onClick={() => void requestCode()}>
              Sign in with an OTP
            </Button>
          </Stack>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            No password yet? Ask the caterer to set one up for you — they can do it in a moment.
          </Typography>

          <Button sx={{ mt: 1 }} onClick={() => navigate('/my/signup')}>
            Create an account
          </Button>
        </CardContent>
      </Card>
      </Box>
    </Box>
  )
}
